import {useMemo, useState, useCallback, useEffect, useRef} from 'react';
import {applySegmentFilters} from '@/utils/segments';
import {
  type Segment,
  type SegmentFilters,
  SEGMENT_SCOPE,
} from '@/types/models/segments/segment';
import {campaignToSegmentFilters} from '@/views/creators/creator-crm/utils/campaignToSegmentFilters';
import useBrandCampaignsQuery, {
  type BrandCampaignsIncludeOption,
  type CampaignListItem,
} from '@/hooks/queries/use-brand-campaigns-query/useBrandCampaignsQuery';

const CRM_SEGMENTS_CAMPAIGNS_INCLUDE: readonly BrandCampaignsIncludeOption[] = [
  'preferred_creator_cities',
  'preferred_creator_states',
  'preferred_creator_countries',
  'preferred_creator_age_detail',
  'preferred_creator_genders',
  'preferred_creator_capabilities',
  'preferred_creator_specialties',
];

export type UseBrandCampaignsSegmentsOptions = {
  merchantId: string;
  brandId?: string;
  enabled?: boolean;
};

export type UseBrandCampaignsSegmentsResult = {
  campaignSegments: Segment[];
  isLoadingCampaigns: boolean;
  selectCampaignSegment: (segment: Segment) => Promise<void>;
};

export default function useBrandCampaignsSegments({
  merchantId,
  brandId,
  enabled = true,
}: UseBrandCampaignsSegmentsOptions): UseBrandCampaignsSegmentsResult {
  const [campaignFiltersCache, setCampaignFiltersCache] = useState<
    Map<string, SegmentFilters>
  >(new Map());
  const preloadedCampaignIdsRef = useRef<Set<string>>(new Set());

  const {brandCampaigns, isLoading: isLoadingCampaigns} =
    useBrandCampaignsQuery({
      brandId: brandId ?? '',
      refetchOnWindowFocus: false,
      states: ['accepting_proposals'],
      include: brandId ? CRM_SEGMENTS_CAMPAIGNS_INCLUDE : undefined,
      enabled: enabled && !!brandId,
    });

  const campaigns = brandCampaigns;

  // Clear preload tracking when brand changes so we build for the new brand's campaigns.
  useEffect(() => {
    preloadedCampaignIdsRef.current.clear();
  }, [brandId]);

  // Preload SegmentFilters for campaigns (Mapbox for locations; disk cache will serve repeated requests).
  useEffect(() => {
    if (campaigns.length === 0) {
      return undefined;
    }
    let cancelled = false;

    (async () => {
      const toBuild = campaigns.filter(
        (c) => !preloadedCampaignIdsRef.current.has(c.id)
      );
      /* eslint-disable no-restricted-syntax, no-await-in-loop -- sequential on purpose to avoid hitting Mapbox in parallel */
      for (const campaign of toBuild) {
        if (cancelled) break;
        preloadedCampaignIdsRef.current.add(campaign.id);
        try {
          const segmentFilters = await campaignToSegmentFilters(campaign);
          if (cancelled) break;
          setCampaignFiltersCache((prev) => {
            const next = new Map(prev);
            next.set(campaign.id, segmentFilters);
            return next;
          });
        } catch {
          preloadedCampaignIdsRef.current.delete(campaign.id);
        }
      }
      /* eslint-enable no-restricted-syntax, no-await-in-loop */
    })();

    return () => {
      cancelled = true;
    };
  }, [campaigns]);

  const campaignSegments = useMemo(
    () =>
      campaigns.map(
        (campaign): Segment => ({
          id: campaign.id,
          name: campaign.name,
          filters: campaignFiltersCache.get(campaign.id) ?? {
            type: 'MultiPlatformFilter',
            profile: undefined,
            platforms: undefined,
            compound: [],
          },
          createdAt: '',
          updatedAt: '',
          createdByDashboardUserId: '',
          agencyId: '',
          merchantId: merchantId || null,
          scope: SEGMENT_SCOPE.BRAND,
          source: 'campaign',
          campaignId: campaign.id,
        })
      ),
    [campaigns, campaignFiltersCache, merchantId]
  );

  const selectCampaignSegment = useCallback(
    async (segment: Segment) => {
      const campaign = campaigns.find((c) => c.id === segment.campaignId);
      if (!campaign) return;

      let segmentFilters = campaignFiltersCache.get(campaign.id);
      if (!segmentFilters) {
        segmentFilters = await campaignToSegmentFilters(campaign);
        setCampaignFiltersCache((prev) => {
          const next = new Map(prev);
          next.set(campaign.id, segmentFilters!);
          return next;
        });
      }
      applySegmentFilters({...segment, filters: segmentFilters});
    },
    [campaigns, campaignFiltersCache]
  );

  return {
    campaignSegments,
    isLoadingCampaigns,
    selectCampaignSegment,
  };
}
